import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatToolbar } from '@angular/material/toolbar';
import {
  PatientCustomFormsService,
  PatientSubmittedFormsListComponent,
} from '@principle-theorem/ng-custom-forms';
import { FeatureEnabledDirective } from '@principle-theorem/ng-feature-flags';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  ButtonsContainerComponent,
  PipesModule,
  TrackByFunctions,
} from '@principle-theorem/ng-shared';
import {
  Appointment,
  AppointmentManager,
  ChecklistItem,
  OrganisationCache,
  PatientForm,
} from '@principle-theorem/principle-core';
import {
  AppointmentStatus,
  ChecklistItemAction,
  ChecklistType,
  IAppointment,
  IChecklistItem,
  IPatient,
  IPatientForm,
  IStaffer,
  PatientFormSpecialUid,
  PatientFormStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  Firestore,
  multiFilter,
  snapshot,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

export interface IInboundChecklistData {
  appointment: WithRef<IAppointment>;
  patient: WithRef<IPatient>;
}

@Component({
  selector: 'pr-inbound-checklist',
  templateUrl: './inbound-checklist.component.html',
  styleUrls: ['./inbound-checklist.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatToolbar,
    MatDialogModule,
    ButtonsContainerComponent,
    MatButtonModule,
    MatListModule,
    PatientSubmittedFormsListComponent,
    MatCheckbox,
    MatIcon,
    PipesModule,
    FeatureEnabledDirective,
  ],
})
export class InboundChecklistComponent {
  trackByItem = TrackByFunctions.ref<IChecklistItem>();
  patient: WithRef<IPatient>;
  practitioner: INamedDocument<IStaffer>;
  appointment$: Observable<WithRef<IAppointment>>;
  checklist$: Observable<WithRef<IChecklistItem>[]>;
  submittedForms$: Observable<WithRef<IPatientForm>[]>;
  isArrived$: Observable<boolean>;
  allComplete$: Observable<boolean>;
  patientForms = PatientFormSpecialUid;
  patientCustomForms = inject(PatientCustomFormsService);

  constructor(
    private _dialogRef: MatDialogRef<
      InboundChecklistComponent,
      AppointmentStatus
    >,
    private _snackBar: MatSnackBar,
    private _organisation: OrganisationService,
    @Inject(MAT_DIALOG_DATA) public data: IInboundChecklistData
  ) {
    this.patient = this.data.patient;
    this.practitioner = this.data.appointment.practitioner;
    this.appointment$ = Firestore.doc$(this.data.appointment.ref);
    this.checklist$ = this.appointment$.pipe(
      switchMap((appointment) =>
        Appointment.checklistItems$(appointment, ChecklistType.Pre)
      )
    );

    this.submittedForms$ = OrganisationCache.patients
      .doc$(this.patient.ref)
      .pipe(
        switchMap((patient) => PatientForm.allPatientForms$(patient)),
        multiFilter(({ status }) => status === PatientFormStatus.Submitted)
      );

    this.allComplete$ = this.checklist$.pipe(
      map((items) => items.every((item) => item.done))
    );

    this.isArrived$ = this.appointment$.pipe(
      map((appointment) => Appointment.isArrived(appointment))
    );
  }

  async markArrival(): Promise<void> {
    const appointment = await snapshot(this.appointment$);
    const staffer: WithRef<IStaffer> = await snapshot(
      this._organisation.staffer$.pipe(filterUndefined())
    );
    await AppointmentManager.markArrived(staffer, appointment);
    this._snackBar.open('Patient marked Arrived, checklist outstanding');
  }

  async checkIn(): Promise<void> {
    const appointment = await snapshot(this.appointment$);
    const staffer: WithRef<IStaffer> = await snapshot(
      this._organisation.staffer$.pipe(filterUndefined())
    );
    await AppointmentManager.markCheckedIn(staffer, appointment);
    this._snackBar.open('Patient checked in');
    this._dialogRef.close(AppointmentStatus.CheckedIn);
  }

  doAction($event: Event, item: IChecklistItem): void {
    $event.stopPropagation();
    switch (item.action) {
      case ChecklistItemAction.Print:
        ChecklistItem.print(item);
        break;
      default:
        break;
    }
  }

  async updateSelected(checklistItem: WithRef<IChecklistItem>): Promise<void> {
    await Firestore.patchDoc(checklistItem.ref, { done: !checklistItem.done });
  }
}
